import { useEffect, useRef, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toReceivedAt, type CeisaVisualNotification, type RiwayatResponNotification } from "@hooks/useCeisaWebSocket";

export interface ApprovalStatusChangedData {
  requestId: number;
  uuid: string;
  ajuNo: string;
  ceisaAjuId: number;
  status: string;
  reviewedByName: string;
  reviewedAt: string;
  checklistItems?: string[];
}

interface ChannelWsMessage {
  type: string;
  processId?: number;
  data?: ApprovalStatusChangedData;
  timestamp?: number;
}

function getChannelWsUrl(channel: string): string {
  const wsBaseUrl = import.meta.env.VITE_WS_BASE_URL as string | undefined;
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const accessToken = localStorage.getItem("accessToken");

  let baseUrl: string;
  if (window.location.hostname === "jar-vis.com" || window.location.hostname.endsWith(".jar-vis.com")) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    baseUrl = `${protocol}//${window.location.host}`;
  } else if (wsBaseUrl) {
    baseUrl = wsBaseUrl.endsWith("/") ? wsBaseUrl.slice(0, -1) : wsBaseUrl;
  } else if (apiBaseUrl) {
    baseUrl = apiBaseUrl.replace(/^http/, "ws").replace(/^https/, "wss");
    if (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
  } else {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    baseUrl = `${protocol}//${window.location.host}`;
  }

  const url = `${baseUrl}/api/v1/ceisa/jarvis-file-status/channel-ws?channel=${channel}`;
  return accessToken ? `${url}&token=${accessToken}` : url;
}

export interface UseApprovalWebSocketReturn {
  visualNotifications: CeisaVisualNotification[];
  removeVisualNotification: (id: string) => void;
}

/**
 * Connects to the channel WebSocket and listens for `approval_status_changed`
 * messages. Returns visual notifications matching the existing CeisaNotification
 * format so they render in the same popup container.
 */
export function useApprovalWebSocket(ppjkNpwp?: string): UseApprovalWebSocketReturn {
  const queryClient = useQueryClient();
  const [visualNotifications, setVisualNotifications] = useState<CeisaVisualNotification[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const removeVisualNotification = useCallback((id: string) => {
    setVisualNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      const ws = wsRef.current;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!ppjkNpwp) return;
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    try {
      const ws = new WebSocket(getChannelWsUrl(ppjkNpwp));
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: ChannelWsMessage = JSON.parse(event.data);

          if (message.type === "approval_status_changed" && message.data) {
            const d = message.data;

            // Invalidate list + stats so table and stat cards refresh
            queryClient.invalidateQueries({ queryKey: ["approval-requests"] });
            queryClient.invalidateQueries({ queryKey: ["approval-request-stats"] });

            // Build a RiwayatResponNotification-compatible object so it renders
            // in the existing CeisaNotificationContainer popup
            const isApproved = d.status === "APPROVED";
            const notification: RiwayatResponNotification = {
              aju_no: d.ajuNo,
              ppjk_npwp: ppjkNpwp,
              timestamp: message.timestamp ?? Date.now(),
              uraian_respon: isApproved ? "APPROVED" : "NEED REVISION",
              kode_respon: isApproved ? "APPROVED" : "NEED_REVISION",
              nama_perusahaan: `Reviewed by ${d.reviewedByName}`,
            };

            const notificationId = `approval-${Date.now()}-${Math.random()}`;
            setVisualNotifications((prev) => [
              ...prev,
              {
                id: notificationId,
                notification,
                receivedAt: toReceivedAt(notification.timestamp),
                durationMs: 15000,
              },
            ]);
          }

          if (message.type === "ping") {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "pong" }));
            }
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onerror = () => {};

      ws.onclose = () => {
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = 5000 * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
        }
      };
    } catch {
      // connection failed
    }
  }, [ppjkNpwp, queryClient]);

  useEffect(() => {
    if (ppjkNpwp) {
      connect();
    }
    return cleanup;
  }, [ppjkNpwp, connect, cleanup]);

  return { visualNotifications, removeVisualNotification };
}
